// KeyPathInput — text input with autocomplete dropdown of payload paths
// inferred from recent preview events. Used everywhere a trigger / view /
// filter wants the operator to point at a field by its dotted path.
//
// The schema is loaded once per source via /api/v1/sources/:id/events:schema
// and cached for 60s in module scope. Dropdown filters as the user types,
// matches in the path are highlighted, and showing all paths (including
// when the field is empty) lets a fresh operator discover the shape of the
// payload without having to open the Schema tab.
//
// Sources with no events yet return zero fields → component degrades to a
// plain input. Schema fetch failures are silent — never block typing.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getEventsSchemaFlat } from '../api';
import type { SchemaField } from '../api';

interface CacheEntry { ts: number; fields: SchemaField[]; sampledEvents: number }
const cache = new Map<string, CacheEntry>();
const CACHE_MS = 60_000;

async function loadFields(sourceId: string, force = false): Promise<{ fields: SchemaField[]; sampledEvents: number }> {
  const hit = cache.get(sourceId);
  if (!force && hit && Date.now() - hit.ts < CACHE_MS) {
    return { fields: hit.fields, sampledEvents: hit.sampledEvents };
  }
  try {
    // Use flat shape: this input auto-completes filter field paths,
    // and filter-eval doesn't dot-traverse plain payload paths
    // (only $origin.X is nested-aware), so suggesting depth-2 paths
    // would mislead the operator.
    const resp = await getEventsSchemaFlat(sourceId);
    cache.set(sourceId, { ts: Date.now(), fields: resp.fields, sampledEvents: resp.sampled_events });
    return { fields: resp.fields, sampledEvents: resp.sampled_events };
  } catch {
    return { fields: [], sampledEvents: 0 };
  }
}

export interface KeyPathInputProps {
  value: string;
  onChange: (next: string) => void;
  /** Source whose schema should drive the suggestions. */
  sourceId: string;
  placeholder?: string;
  /** Optional inline style override; defaults match the trigger Inp helper. */
  style?: React.CSSProperties;
  /** Filter the suggestion list — e.g. only show paths whose value type
   *  is numeric for parse_float / float-only fields. */
  typeFilter?: 'any' | 'numeric' | 'string' | 'object';
  /** Disable the dropdown entirely; falls back to a plain input. */
  noSuggest?: boolean;
}

export default function KeyPathInput(props: KeyPathInputProps): React.ReactElement {
  const { value, onChange, sourceId, placeholder, style, typeFilter = 'any', noSuggest = false } = props;
  const [fields, setFields] = useState<SchemaField[]>([]);
  const [sampled, setSampled] = useState(0);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!sourceId) return;
    void loadFields(sourceId).then(r => { setFields(r.fields); setSampled(r.sampledEvents); });
  }, [sourceId]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent): void {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const filtered = useMemo(() => {
    const q = value.trim().toLowerCase();
    return fields
      .filter(f => {
        if (typeFilter === 'any') return true;
        if (typeFilter === 'numeric') return f.types.some(t => t === 'number' || t === 'integer' || t === 'float');
        if (typeFilter === 'string') return f.types.includes('string');
        if (typeFilter === 'object') return f.types.includes('object') || f.types.includes('array');
        return true;
      })
      .filter(f => !q || f.path.toLowerCase().includes(q))
      .sort((a, b) => b.occurrences - a.occurrences)
      .slice(0, 30);
  }, [fields, value, typeFilter]);

  const baseStyle: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box',
    padding: '8px 12px', fontSize: 13, fontFamily: 'var(--ui)',
    border: '1px solid var(--line)', borderRadius: 6, outline: 'none',
    background: 'var(--bg)',
    ...style,
  };

  if (noSuggest) {
    return (
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={baseStyle} />
    );
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        onFocus={() => setOpen(true)}
        onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }}
        placeholder={placeholder}
        style={baseStyle}
      />
      {open && fields.length > 0 && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 30,
          background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 6,
          boxShadow: '0 8px 24px oklch(0.10 0 0 / 0.15)',
          maxHeight: 280, overflowY: 'auto',
        }}>
          <div style={{ padding: '6px 12px', fontSize: 10.5, color: 'var(--ink-3)', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid var(--line)' }}>
            paths in last {sampled} events {filtered.length < fields.length ? `· ${filtered.length}/${fields.length}` : ''}
          </div>
          {filtered.length === 0 && (
            <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--ink-3)' }}>
              No paths match your filter.
            </div>
          )}
          {filtered.map(f => (
            <button
              key={f.path}
              onMouseDown={e => { e.preventDefault(); onChange(f.path); setOpen(false); }}
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                gap: 8, width: '100%', padding: '6px 12px',
                fontFamily: 'var(--mono)', fontSize: 12,
                background: 'transparent', color: 'var(--ink)', border: 'none', cursor: 'pointer',
                textAlign: 'left',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-elev)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <span>{f.path}</span>
              <span style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>
                {f.types.join('|')}{f.occurrences < sampled ? ` · ${Math.round((f.occurrences / sampled) * 100)}%` : ''}
              </span>
            </button>
          ))}
        </div>
      )}
      {open && fields.length === 0 && sourceId && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 30,
          background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 6,
          padding: '10px 12px', fontSize: 12, color: 'var(--ink-3)',
        }}>
          {sampled === 0 ? 'No events yet — send a sample event to see field suggestions here.' : 'Schema unavailable.'}
        </div>
      )}
    </div>
  );
}
